import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { InputTextarea } from 'primereact/inputtextarea';
import { Dialog } from 'primereact/dialog';
import { Dialog as PrimeDialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Menu } from 'primereact/menu';
import { FileUpload } from 'primereact/fileupload';
import { ColorPicker } from 'primereact/colorpicker';
import { Checkbox } from 'primereact/checkbox';
import { Tooltip } from 'primereact/tooltip';
import { Skeleton } from 'primereact/skeleton';
import { classNames } from 'primereact/utils';
import { 
    selectLoading, 
    selectPartnerList, 
    selectPartnerListCount, 
    getPartnersAsync, 
    getPartnersCountAsync, 
    deletePartnerAsync, 
    editPartnerAsync,
    createPartnerAsync,
    initialLoadAsync,
} from './partnerSlice';
import { errorService } from '../../services/ErrorService';
import { showErrorToast, showSuccessToast } from '../../utils/ToastUtils';
import { PartnerService } from '../../service/PartnerService';
import PartnerImage from '../../components/PartnerImage';

const emptyPartner = {
    id: null,
    name: "",
    title: "",
    subtitle: "",
    link: "",
    image: "",
    logo: "",
    color: "",
    active: false
};

const PartnersView = () => {

    const defaultGridParameters = {page: 0, first: 0, rows: 10, sortField: 'id', sortOrder: 1, filters: {}}
    
    const [deletePartnerDialog, setDeletePartnerDialog] = useState(false);
    const [partnerDialog, setPartnerDialog] = useState(false);
    const [partner, setPartner] = useState(emptyPartner);
    const [selectedPartners, setSelectedPartners] = useState(null);
    const [gridParameters, setGridParameters] = useState(defaultGridParameters);
    const [submitted, setSubmitted] = useState(false);
    const [fileSelectedImage, setFileSelectedImage] = useState(false);
    const [fileSelectedLogo, setFileSelectedLogo] = useState(false);
    const [imageFile, setImageFile] = useState(null);
    const [logoFile, setLogoFile] = useState(null);
    const [imagePreview, setImagePreview] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [maximized, setMaximized] = useState(true);
    const [imageRefresh, setImageRefresh] = useState(Date.now());
    const [originalImage, setOriginalImage] = useState(null);
    const [originalLogo, setOriginalLogo] = useState(null);
    const [imageDialog, setImageDialog] = useState(false);
    const [selectedImage, setSelectedImage] = useState(null);

    const toast = useRef(null);
    const dt = useRef(null);
    const menu = useRef(null);
    const [detail, setDetail] = useState({});
    const partnerService = new PartnerService();

    const dispatch = useDispatch();
    const loading = useSelector(selectLoading);
    const partnerList = useSelector(selectPartnerList);
    const partnerListCount = useSelector(selectPartnerListCount);

    useEffect(() => {
        const loadInitialData = async () => {
            try {
                await errorService.retryOperation(async () => {
                    await dispatch(initialLoadAsync(defaultGridParameters)).unwrap();
                });
            } catch (error) {
                showErrorToast(toast, 'Erro ao carregar parceiros', error);
            }
        };

        loadInitialData();
    }, [dispatch]);

    const onSort = async (event) => {
        try {
            await errorService.retryOperation(async () => {
                const sortParams = {
                    ...gridParameters,
                    sortField: event.sortField,
                    sortOrder: event.sortOrder
                };
                setGridParameters(sortParams);
                await Promise.all([
                    dispatch(getPartnersAsync(sortParams)).unwrap(),
                    dispatch(getPartnersCountAsync(sortParams)).unwrap()
                ]);
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao ordenar parceiros', error);
        }
    };

    const onFilter = async (event) => {
        try {
            await errorService.retryOperation(async () => {
                const filterParams = {
                    ...gridParameters,
                    filters: event.filters
                };
                setGridParameters(filterParams);
                await Promise.all([
                    dispatch(getPartnersAsync(filterParams)).unwrap(),
                    dispatch(getPartnersCountAsync(filterParams)).unwrap()
                ]);
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao filtrar parceiros', error);
        }
    };

    const onPage = async (event) => {
        try {
            await errorService.retryOperation(async () => {
                const pageParams = {
                    ...gridParameters,
                    first: event.first,
                    rows: event.rows
                };
                setGridParameters(pageParams);
                await dispatch(getPartnersAsync(pageParams)).unwrap();
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao mudar página', error);
        }
    };

    const openNew = () => {
        setPartner({...emptyPartner});
        setSubmitted(false);
        setFileSelectedImage(false);
        setFileSelectedLogo(false);
        setImageFile(null);
        setLogoFile(null);
        setImagePreview(null);
        setLogoPreview(null);
        setOriginalImage(null);
        setOriginalLogo(null);
        setPartnerDialog(true);
    }

    const hideDialog = () => {
        setPartnerDialog(false);
        setSubmitted(false);
        setFileSelectedImage(false);
        setFileSelectedLogo(false);
        setImageFile(null);
        setLogoFile(null);
        setImagePreview(null);
        setLogoPreview(null);
    }

    const hideDeletePartnerDialog = () => {
        setDeletePartnerDialog(false);
    }

    const savePartner = async () => {
        setSubmitted(true);

        if (partner.name && partner.name.trim()) {
            try {
                await errorService.retryOperation(async () => {
                    if (partner.id) {
                        await dispatch(editPartnerAsync({partner, imageFile, logoFile, originalImage, originalLogo, gridParameters})).unwrap();
                        showSuccessToast(toast, 'Sucesso', 'Parceiro atualizado com sucesso');
                    } else {
                        await dispatch(createPartnerAsync({partner, imageFile, logoFile, gridParameters})).unwrap();
                        showSuccessToast(toast, 'Sucesso', 'Parceiro criado com sucesso');
                    }
                    
                    hideDialog();
                    setImageRefresh(Date.now()); // Atualiza o cache das imagens
                    await dispatch(getPartnersAsync(gridParameters)).unwrap();
                });
            } catch (error) {
                showErrorToast(toast, 'Erro ao salvar parceiro', error);
            }
        }
    };

    const editPartner = (partner) => {
        setPartner({ ...partner });
        setFileSelectedImage(false);
        setFileSelectedLogo(false);
        setImageFile(null);
        setLogoFile(null);
        setImagePreview(null);
        setLogoPreview(null);
        // Guarda os valores originais para comparar se foram removidos
        setOriginalImage(partner.image);
        setOriginalLogo(partner.logo);
        setPartnerDialog(true);
    }

    const confirmDeletePartner = (partner) => {
        setPartner(partner);
        setDeletePartnerDialog(true);
    }

    const deletePartner = async () => {
        try {
            await errorService.retryOperation(async () => {
                await dispatch(deletePartnerAsync({id: partner.id, gridParameters})).unwrap();
                showSuccessToast(toast, 'Sucesso', 'Parceiro excluído com sucesso');
                hideDeletePartnerDialog();
                await dispatch(getPartnersAsync(gridParameters)).unwrap();
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao excluir parceiro', error);
        }
    };

    const exportCSV = () => {
        dt.current.exportCSV();
    }

    const onInputChangePartner = (e, name) => {
        const val = (e.target && e.target.value) || '';
        let _partner = { ...partner };
        _partner[`${name}`] = val;
        setPartner(_partner);
    }

    const onColorChange = (e) => {
        let _partner = { ...partner };
        // Remove o # do valor da cor
        _partner.color = e.value ? e.value.replace('#', '') : '';
        setPartner(_partner);
    }

    const onActiveChange = (e) => {
        let _partner = { ...partner };
        _partner.active = e.checked;
        setPartner(_partner);
    }

    const leftToolbarTemplate = () => {
        return (
            <React.Fragment>
                <div className="my-2">
                    <Button label="Novo parceiro" icon="pi pi-plus" className="p-button-success mr-2" onClick={openNew} />
                </div>
            </React.Fragment>
        )
    }

    const toggleMenu = (event, data) => {
        setDetail(data)
        menu.current.toggle(event);
    };

    const toolbarItems = [
        {
            label: 'Editar',
            icon: 'pi pi-pencil',
            command: () => editPartner(detail)
        },
        {
            label: 'Excluir',
            icon: 'pi pi-trash',
            command: () => confirmDeletePartner(detail)
        }
    ];

    const actionBodyTemplate = (rowData) => {
        return <>
            <Menu ref={menu} model={toolbarItems} popup />
            <Button type="button" label="Ações" icon="pi pi-angle-down" onClick={(event) => toggleMenu(event, rowData)} style={{ width: 'auto' }} />
        </>;
    }

    const activeBodyTemplate = (rowData) => {
        return (
            <>
                <span className="p-column-title">Ativo</span>
                {rowData.active ? 'Sim' : 'Não'}
            </>
        );
    }

    const colorBodyTemplate = (rowData) => {
        if (!rowData.color) return null;
        return (
            <>
                <span className="p-column-title">Cor</span>
                <div style={{ 
                    width: '30px', 
                    height: '30px', 
                    backgroundColor: `#${rowData.color}`, 
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    display: 'inline-block'
                }}></div>
            </>
        );
    }

    const imageBodyTemplate = (rowData) => {
        if (!rowData.image) return null;
        return (
            <PartnerImage
                partnerId={rowData.id}
                type="image"
                width={60}
                refresh={imageRefresh}
                className="shadow-2"
                style={{ borderRadius: '8px', border: '2px solid #eee' }}
                onClick={(src) => { setSelectedImage(src); setImageDialog(true); }}
            />
        );
    }

    const logoBodyTemplate = (rowData) => {
        if (!rowData.logo) return null;
        return (
            <PartnerImage
                partnerId={rowData.id}
                type="logo"
                width={60}
                refresh={imageRefresh}
                className="shadow-2"
                style={{ borderRadius: '8px', border: '2px solid #eee' }}
                onClick={(src) => { setSelectedImage(src); setImageDialog(true); }}
            />
        );
    }

    const partnerDialogFooter = (
        <>
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text" onClick={hideDialog} />
            <Button label="Salvar" icon="pi pi-check" className="p-button-text" onClick={savePartner} />
        </>
    );

    const deletePartnerDialogFooter = (
        <>
            <Button label="Não" icon="pi pi-times" className="p-button-text" onClick={hideDeletePartnerDialog} />
            <Button label="Sim" icon="pi pi-check" className="p-button-text" onClick={deletePartner} />
        </>
    );

    const handleImageSelect = (e) => {
        if (e.files && e.files.length > 0) {
            const file = e.files[0];
            setImageFile(file);
            setFileSelectedImage(true);
            // Cria preview da imagem
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    }

    const handleLogoSelect = (e) => {
        if (e.files && e.files.length > 0) {
            const file = e.files[0];
            setLogoFile(file);
            setFileSelectedLogo(true);
            // Cria preview do logo
            const reader = new FileReader();
            reader.onloadend = () => {
                setLogoPreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    }

    const onRefresh = async () => {
        try {
            await errorService.retryOperation(async () => {
                await Promise.all([
                    dispatch(getPartnersAsync(gridParameters)).unwrap(),
                    dispatch(getPartnersCountAsync(gridParameters)).unwrap()
                ]);
            });
        } catch (error) {
            showErrorToast(toast, 'Erro ao carregar dados', error);
        }
    };

    return (
      <>
        <div className="grid crud-demo">
            <div className="col-12">
                <div className="card">
                    <Toast ref={toast} />
                    <div className="flex justify-content-between align-items-center mb-3">
                        <h5 className="m-0">Gestão de parceiros</h5>
                        <Button
                            icon="pi pi-refresh"
                            rounded
                            text
                            onClick={onRefresh}
                            disabled={loading}
                        />
                    </div>
                    <DataTable ref={dt} value={loading ? Array.from({ length: gridParameters.rows }, (_, i) => ({ id: `skeleton-${i}` })) : partnerList} selection={selectedPartners} onSelectionChange={(e) => setSelectedPartners(e.value)}
                        header={leftToolbarTemplate()}
                        dataKey="id"
                        paginator
                        sortField={gridParameters.sortField}
                        sortOrder={gridParameters.sortOrder}
                        rows={gridParameters.rows}
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        className="datatable-responsive"
                        paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
                        currentPageReportTemplate="Exibindo de {first} à {last} de {totalRecords} registros"
                        emptyMessage="Sem resultados."
                        responsiveLayout="scroll"
                        totalRecords={partnerListCount}
                        lazy={true}
                        first={gridParameters.first}
                        onSort={(e) => onSort(e)}
                        onFilter={(e) => onFilter(e)}
                        onPage={(e) => onPage(e)}
                        >
                        <Column field="name" header="Nome" sortable={!loading} body={loading ? () => <Skeleton /> : undefined} headerStyle={{ width: '15%', minWidth: '12rem' }}></Column>
                        <Column field="title" header="Título" sortable={!loading} body={loading ? () => <Skeleton /> : undefined} headerStyle={{ width: '15%', minWidth: '12rem' }}></Column>
                        <Column field="subtitle" header="Subtítulo" sortable={!loading} body={loading ? () => <Skeleton /> : undefined} headerStyle={{ width: '15%', minWidth: '12rem' }}></Column>
                        <Column field="link" header="Link" sortable={!loading} body={loading ? () => <Skeleton /> : undefined} headerStyle={{ width: '15%', minWidth: '12rem' }}></Column>
                        <Column header="Banner" body={loading ? () => <Skeleton /> : imageBodyTemplate} headerStyle={{ width: '8rem' }} style={{ width: '8rem' }}></Column>
                        <Column header="Logo" body={loading ? () => <Skeleton /> : logoBodyTemplate} headerStyle={{ width: '8rem' }} style={{ width: '8rem' }}></Column>
                        <Column field="color" header="Cor" body={loading ? () => <Skeleton /> : colorBodyTemplate} headerStyle={{ width: '8rem' }} style={{ width: '8rem' }}></Column>
                        <Column field="active" header="Ativo" body={loading ? () => <Skeleton /> : activeBodyTemplate} headerStyle={{ width: '8rem' }} style={{ width: '8rem' }}></Column>
                        <Column body={loading ? () => <Skeleton /> : actionBodyTemplate} headerStyle={{ width: '10rem' }} style={{ width: '10rem' }}></Column>
                    </DataTable>

                    {/* Editar Parceiro */}
                    <Dialog 
                        visible={partnerDialog} 
                        style={{ width: '80vw', maxWidth: '800px' }} 
                        header={`${partner.id ? 'Editar' : 'Criar'} parceiro`} 
                        modal 
                        className="p-fluid" 
                        footer={partnerDialogFooter} 
                        onHide={hideDialog}
                        maximizable
                        maximized={maximized}
                        onMaximize={(e) => setMaximized(e.maximized)}
                        blockScroll={false}
                    >
                        <div className="grid">
                            {/* Nome */}
                            <div className="col-12">
                                <div className="field">
                                    <label htmlFor="name">Nome *</label>
                                    <InputText id="name" value={partner.name} onChange={(e) => onInputChangePartner(e, 'name')} required className={classNames({ 'p-invalid': submitted && !partner.name })} />
                                    {submitted && !partner.name && <small className="p-invalid">Nome é obrigatório.</small>}
                                </div>
                            </div>

                            {/* Título e Subtítulo */}
                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="title">Título</label>
                                    <InputText id="title" value={partner.title} onChange={(e) => onInputChangePartner(e, 'title')} />
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="subtitle">Subtítulo</label>
                                    <InputText id="subtitle" value={partner.subtitle} onChange={(e) => onInputChangePartner(e, 'subtitle')} />
                                </div>
                            </div>

                            {/* Link */}
                            <div className="col-12">
                                <div className="field">
                                    <label htmlFor="link">Link (URL)</label>
                                    <InputText id="link" value={partner.link} onChange={(e) => onInputChangePartner(e, 'link')} placeholder="https://exemplo.com" />
                                </div>
                            </div>

                            {/* Cor e Ativo */}
                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="color">Cor</label>
                                    <div className="flex align-items-center gap-2">
                                        <ColorPicker 
                                            value={partner.color ? `#${partner.color}` : '#000000'} 
                                            onChange={onColorChange} 
                                            style={{ width: '3rem', height: '3rem' }} 
                                        />
                                        <InputText 
                                            id="color" 
                                            value={partner.color ? `#${partner.color}` : ''} 
                                            onChange={(e) => {
                                                const colorValue = e.target.value.replace('#', '');
                                                let _partner = { ...partner };
                                                _partner.color = colorValue;
                                                setPartner(_partner);
                                            }}
                                            placeholder="#000000"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label className="mb-3">Status</label>
                                    <div className="flex align-items-center">
                                        <Checkbox inputId="active" checked={partner.active} onChange={onActiveChange} />
                                        <label htmlFor="active" className="ml-2">Ativo</label>
                                    </div>
                                </div>
                            </div>

                            {/* Upload de Imagens */}
                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="image">Banner</label>
                                    <FileUpload 
                                        mode="basic" 
                                        accept="image/*" 
                                        maxFileSize={10000000} 
                                        chooseLabel={fileSelectedImage ? "Banner selecionado" : "Selecionar Banner"}
                                        onSelect={handleImageSelect}
                                        onClear={() => {
                                            setFileSelectedImage(false);
                                            setImageFile(null);
                                            setImagePreview(null);
                                            let _partner = { ...partner };
                                            _partner.image = '';
                                            setPartner(_partner);
                                        }}
                                    />
                                    {/* Preview do Banner */}
                                    {imagePreview && (
                                        <div className="mt-3 text-center">
                                            <div className="relative" style={{ display: 'inline-block', marginBottom: '8px' }}>
                                                <img 
                                                    src={imagePreview} 
                                                    alt="Preview Banner" 
                                                    width={80} 
                                                    height={80}
                                                    className="shadow-2" 
                                                    style={{ 
                                                        borderRadius: '8px', 
                                                        border: '2px solid #eee',
                                                        objectFit: 'cover'
                                                    }}
                                                />
                                                <Button 
                                                    icon="pi pi-times" 
                                                    className="p-button-rounded p-button-danger p-button-sm" 
                                                    style={{ 
                                                        position: 'absolute', 
                                                        top: '-10px', 
                                                        right: '-10px',
                                                        width: '24px',
                                                        height: '24px'
                                                    }}
                                                    onClick={() => {
                                                        setFileSelectedImage(false);
                                                        setImageFile(null);
                                                        setImagePreview(null);
                                                        let _partner = { ...partner };
                                                        _partner.image = '';
                                                        setPartner(_partner);
                                                    }}
                                                    tooltip="Remover banner"
                                                    tooltipOptions={{ position: 'top' }}
                                                />
                                            </div>
                                            <p className="text-xs text-600 mt-2">Preview do Banner</p>
                                        </div>
                                    )}
                                    {partner.image && !fileSelectedImage && !imagePreview && (
                                        <div className="mt-3 text-center">
                                            <div className="relative" style={{ display: 'inline-block', marginBottom: '8px' }}>
                                                <PartnerImage
                                                    partnerId={partner.id}
                                                    type="image"
                                                    width={80}
                                                    height={80}
                                                    refresh={imageRefresh}
                                                    className="shadow-2 cursor-pointer"
                                                    style={{ borderRadius: '8px', border: '2px solid #eee', objectFit: 'cover' }}
                                                />
                                                <Button 
                                                    icon="pi pi-times" 
                                                    className="p-button-rounded p-button-danger p-button-sm" 
                                                    style={{ 
                                                        position: 'absolute', 
                                                        top: '-10px', 
                                                        right: '-10px',
                                                        width: '24px',
                                                        height: '24px'
                                                    }}
                                                    onClick={() => {
                                                        let _partner = { ...partner };
                                                        _partner.image = '';
                                                        setPartner(_partner);
                                                    }}
                                                    tooltip="Remover banner"
                                                    tooltipOptions={{ position: 'top' }}
                                                />
                                            </div>
                                            <p className="text-xs text-600 mt-2">Banner atual</p>
                                        </div>
                                    )}
                                </div>
                            </div>

                            <div className="col-12 md:col-6">
                                <div className="field">
                                    <label htmlFor="logo">Logo</label>
                                    <FileUpload 
                                        mode="basic" 
                                        accept="image/*" 
                                        maxFileSize={10000000} 
                                        chooseLabel={fileSelectedLogo ? "Logo selecionado" : "Selecionar Logo"}
                                        onSelect={handleLogoSelect}
                                        onClear={() => {
                                            setFileSelectedLogo(false);
                                            setLogoFile(null);
                                            setLogoPreview(null);
                                            let _partner = { ...partner };
                                            _partner.logo = '';
                                            setPartner(_partner);
                                        }}
                                    />
                                    {/* Preview do Logo */}
                                    {logoPreview && (
                                        <div className="mt-3 text-center">
                                            <div className="relative" style={{ display: 'inline-block', marginBottom: '8px' }}>
                                                <img 
                                                    src={logoPreview} 
                                                    alt="Preview Logo" 
                                                    width={80} 
                                                    height={80}
                                                    className="shadow-2" 
                                                    style={{ 
                                                        borderRadius: '8px', 
                                                        border: '2px solid #eee',
                                                        objectFit: 'cover'
                                                    }}
                                                />
                                                <Button 
                                                    icon="pi pi-times" 
                                                    className="p-button-rounded p-button-danger p-button-sm" 
                                                    style={{ 
                                                        position: 'absolute', 
                                                        top: '-10px', 
                                                        right: '-10px',
                                                        width: '24px',
                                                        height: '24px'
                                                    }}
                                                    onClick={() => {
                                                        setFileSelectedLogo(false);
                                                        setLogoFile(null);
                                                        setLogoPreview(null);
                                                        let _partner = { ...partner };
                                                        _partner.logo = '';
                                                        setPartner(_partner);
                                                    }}
                                                    tooltip="Remover logo"
                                                    tooltipOptions={{ position: 'top' }}
                                                />
                                            </div>
                                            <p className="text-xs text-600 mt-2">Preview do Logo</p>
                                        </div>
                                    )}
                                    {partner.logo && !fileSelectedLogo && !logoPreview && (
                                        <div className="mt-3 text-center">
                                            <div className="relative" style={{ display: 'inline-block', marginBottom: '8px' }}>
                                                <PartnerImage
                                                    partnerId={partner.id}
                                                    type="logo"
                                                    width={80}
                                                    height={80}
                                                    refresh={imageRefresh}
                                                    className="shadow-2 cursor-pointer"
                                                    style={{ borderRadius: '8px', border: '2px solid #eee', objectFit: 'cover' }}
                                                />
                                                <Button 
                                                    icon="pi pi-times" 
                                                    className="p-button-rounded p-button-danger p-button-sm" 
                                                    style={{ 
                                                        position: 'absolute', 
                                                        top: '-10px', 
                                                        right: '-10px',
                                                        width: '24px',
                                                        height: '24px'
                                                    }}
                                                    onClick={() => {
                                                        let _partner = { ...partner };
                                                        _partner.logo = '';
                                                        setPartner(_partner);
                                                    }}
                                                    tooltip="Remover logo"
                                                    tooltipOptions={{ position: 'top' }}
                                                />
                                            </div>
                                            <p className="text-xs text-600 mt-2">Logo atual</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </Dialog>
                    {/* FIM - Editar Parceiro */}

                    <Dialog visible={deletePartnerDialog} style={{ width: '450px' }} header="Confirmar exclusão" modal footer={deletePartnerDialogFooter} onHide={hideDeletePartnerDialog}>
                        <div className="flex align-items-center justify-content-center">
                            <i className="pi pi-exclamation-triangle mr-3" style={{ fontSize: '2rem' }} />
                            {partner && <span>Você deseja realmente excluir: <b>{partner.name}</b>?</span>}
                        </div>
                    </Dialog>

                    <PrimeDialog header="Visualizar Banner" visible={imageDialog} style={{ width: '50vw' }} modal onHide={() => setImageDialog(false)} closable={true}>
                        {selectedImage && (
                            <img src={selectedImage} alt="Parceiro" style={{ width: '100%', borderRadius: '12px' }} />
                        )}
                    </PrimeDialog>
                </div>
            </div>
        </div>
      </>
    );
}

const comparisonFn = function (prevProps, nextProps) {
    return prevProps.location.pathname === nextProps.location.pathname;
};

export default React.memo(PartnersView, comparisonFn);

